/**
 * A sessão de staff, num sítio só.
 *
 * Cada ecrã lia o token do localStorage e redirecionava sozinho para o
 * login. Cinco cópias da mesma decisão é cinco sítios para esquecer um
 * caso — e um deles esquecia mesmo, deixando o ecrã em branco em vez de
 * mandar para o login.
 *
 * Guarda também que restaurantes a conta serve, porque o servidor precisa
 * de saber em qual se está a trabalhar (cabeçalho X-Tenant).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { api, clearAuthToken, getAuthToken, isAuthError, SESSION_EXPIRED_EVENT, setAuthToken } from '../api/client';
import { clearTenantSlug, setTenantSlug } from '../api/tenant';
import Loader from '../components/common/Loader';
import { disablePushNotifications } from '../lib/pushNotifications';
import { withDeadline } from '../lib/serviceWorker';
import { suspendStaffOffline } from '../lib/staffOffline';
import type { LoginResponse } from '../types/domain';

const STORAGE_KEY = 'sessao_bamidi';

interface SessionContextValue {
  sessao: LoginResponse | null;
  aCarregar: boolean;
  iniciarSessao: (username: string, password: string) => Promise<void>;
  terminarSessao: () => Promise<void>;
  escolherRestaurante: (slug: string) => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

const lerGuardada = (): LoginResponse | null => {
  try {
    const bruto = localStorage.getItem(STORAGE_KEY);
    if (!bruto) return null;
    const saved = JSON.parse(bruto) as Partial<LoginResponse> | null;
    if (saved && typeof saved.user === 'string' && typeof saved.token === 'string'
      && Array.isArray(saved.tenants) && saved.tenants.length > 0 && saved.tenants.every((tenant) =>
        tenant && typeof tenant.slug === 'string' && typeof tenant.name === 'string'
        && ['owner', 'manager', 'waiter', 'kitchen', 'superuser'].includes(tenant.role))) {
      if (getAuthToken() === saved.token) {
        // Restore the restaurant before any staff screen can send a request.
        setTenantSlug(saved.tenants[0]!.slug);
        return saved as LoginResponse;
      }
    }
    clearAuthToken();
    clearTenantSlug();
    localStorage.removeItem(STORAGE_KEY);
    return null;
  } catch {
    clearAuthToken();
    clearTenantSlug();
    return null;
  }
};

export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const [guardada] = useState(lerGuardada);
  const [sessao, setSessao] = useState<LoginResponse | null>(guardada);
  const [aRestaurar, setARestaurar] = useState(Boolean(guardada));
  const [aCarregar, setACarregar] = useState(false);

  const limparSessao = useCallback(() => {
    void suspendStaffOffline();
    clearAuthToken();
    clearTenantSlug();
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* Storage may be unavailable. */ }
    setSessao(null);
  }, []);

  useEffect(() => {
    window.addEventListener(SESSION_EXPIRED_EVENT, limparSessao);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, limparSessao);
  }, [limparSessao]);

  // Uma sessão guardada sem token é lixo de um logout a meio: limpar em vez
  // de deixar a aplicação a julgar-se autenticada.
  useEffect(() => {
    if (sessao && !getAuthToken()) {
      setSessao(null);
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* Storage may be unavailable. */ }
    }
  }, [sessao]);

  const guardar = useCallback((dados: LoginResponse) => {
    setAuthToken(dados.token);
    if (dados.tenants[0]) setTenantSlug(dados.tenants[0].slug);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dados));
    } catch {
      // Modo privado: a sessão dura esta página.
    }
    setSessao(dados);
  }, []);

  useEffect(() => {
    if (!guardada) return;
    let ativo = true;
    // Memberships can change while the app is closed. /me is independent
    // of X-Tenant, so a stale selection cannot prevent session recovery.
    void api.get<{ username: string; is_staff: boolean; tenants: LoginResponse['tenants'] }>(
      '/api/me/', { timeout: 10_000 },
    ).then(({ data }) => {
      if (!ativo || getAuthToken() !== guardada.token) return;
      if (!data.tenants.length) { limparSessao(); return; }
      const escolhido = data.tenants.find((tenant) => tenant.slug === guardada.tenants[0]?.slug);
      guardar({
        ...guardada, user: data.username, is_staff: data.is_staff,
        tenants: escolhido
          ? [escolhido, ...data.tenants.filter((tenant) => tenant.slug !== escolhido.slug)]
          : data.tenants,
      });
    }).catch((error: unknown) => {
      if (ativo && getAuthToken() === guardada.token && isAuthError(error)) limparSessao();
      // Keep the saved session available for the staff app's offline mode
      // when the server cannot be reached. It still enforces access on sync.
    }).finally(() => { if (ativo) setARestaurar(false); });
    return () => { ativo = false; };
  }, [guardada, guardar, limparSessao]);

  const iniciarSessao = useCallback(
    async (username: string, password: string) => {
      setACarregar(true);
      try {
        const { data } = await api.post<LoginResponse>('/api/login/', {
          username,
          password,
        }, { timeout: 15_000 });
        guardar(data);
      } finally {
        setACarregar(false);
      }
    },
    [guardar],
  );

  const terminarSessao = useCallback(async () => {
    await suspendStaffOffline();
    await withDeadline(disablePushNotifications(), 'Não foi possível desligar as notificações.', 5000).catch(() => undefined);
    await api.post('/api/logout/', undefined, { timeout: 5000 }).catch(() => undefined);
    limparSessao();
  }, [limparSessao]);

  const escolherRestaurante = useCallback((slug: string) => {
    const escolhido = sessao?.tenants.find((tenant) => tenant.slug === slug);
    if (!sessao || !escolhido) return;
    guardar({ ...sessao, tenants: [escolhido, ...sessao.tenants.filter((tenant) => tenant.slug !== slug)] });
  }, [sessao, guardar]);

  const valor = useMemo(
    () => ({ sessao, aCarregar, iniciarSessao, terminarSessao, escolherRestaurante }),
    [sessao, aCarregar, iniciarSessao, terminarSessao, escolherRestaurante],
  );

  return <SessionContext.Provider value={valor}>{aRestaurar ? <Loader texto="A recuperar a sessão…" /> : children}</SessionContext.Provider>;
};

export const useSessao = (): SessionContextValue => {
  const contexto = useContext(SessionContext);
  if (!contexto) {
    throw new Error('useSessao fora de <SessionProvider>.');
  }
  return contexto;
};
